<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import {
  CreateOrUpdateProtocolDtoTypeEnum,
  ProtocolDto,
  ProtocolDtoTypeEnum,
} from '@/api/generator';
import ProtocolForm from './components/ProtocolForm.vue';
import { useLoading } from '@/utils/useLoading';
import { onMounted } from 'vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useToken } from '@/utils/tokens/tokens';
import { useRouter } from 'vue-router';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { backendValidator } from '@/validation/validators/backendValidator';

export interface ProtocolEditViewProps {
  id: string;
}

const props = defineProps<ProtocolEditViewProps>();
const protocolStore = useProtocolsStore();
const router = useRouter();

const { action: loadProtocol, loading } = useLoading(async () => {
  const { result } = await api.protocolsApi.getProtocol({ id: props.id }, [
    entityNotFoundErrorHandler(router),
  ]);
  if (result) {
    resetForm({ values: result });
  }
}, true);

const token = useToken({
  id: props.id,
  $type:
    ProtocolDtoTypeEnum.PtwPtw100AppZeroApplicationSharedProtocolsDtoProtocolDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(loadProtocol);

token.on.inUse(() => {
  router.push({
    name: routenames.protocols.overview,
  });
});

const { form } = useVeeForm<ProtocolDto>({
  schemaName: 'ProtocolDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((createOrUpdateProtocolDto, errorHandlers) =>
      api.protocolsApi.validateProtocol(
        {
          createOrUpdateProtocolDto: {
            ...createOrUpdateProtocolDto,
            $type:
              CreateOrUpdateProtocolDtoTypeEnum.PtwPtw100AppZeroApplicationSharedProtocolsDtoCreateOrUpdateProtocolDtoPtwPtw100AppZeroApplicationShared,
          },
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values, resetForm } = form;

const submit = handleSubmit(
  async (createOrUpdateProtocolDto) => {
    const { success } = await api.protocolsApi.updateProtocol(
      {
        id: props.id,
        createOrUpdateProtocolDto: {
          ...createOrUpdateProtocolDto,
          $type:
            CreateOrUpdateProtocolDtoTypeEnum.PtwPtw100AppZeroApplicationSharedProtocolsDtoCreateOrUpdateProtocolDtoPtwPtw100AppZeroApplicationShared,
        },
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await protocolStore.update();
      await router.push({
        name: routenames.protocols.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :subtitle="$t('Protocol_one')" :title="values.name" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.protocols.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <ProtocolForm />
    </BasePage>
  </form>
</template>
